import { useEffect } from "react";
import { createContext, useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { Outlet, useNavigate } from "react-router-dom";
import { addMultipleUsers } from "../../features/admin/adminSlice";
import { updateMenu } from "../../features/menu/menuSlice";
import { getDataStats } from "../../API/Outputs";
import { selectLabels } from "../../constants/appConstants";

export const AuthContext = createContext();

const AuthProvider = () => {
  const [user, setUser] = useState(null);
  const [selectedUnmet, setSelectedUnmet] = useState([]);

  const [accessToken, setAccessToken] = useState(
    localStorage.getItem("accessToken")
  );
  const [refreshToken, setRefreshToken] = useState(
    localStorage.getItem("refreshToken")
  );

  // Compute isAuthenticated based on presence of accessToken and user
  const isAuthenticated = useMemo(() => {
    return !!(accessToken && user);
  }, [accessToken, user]);

  const dispatch = useDispatch();

  useEffect(() => {
    // Check if tokens are already stored in local storage on page load
    const storedAccessToken = localStorage.getItem("accessToken");
    const storedRefreshToken = localStorage.getItem("refreshToken");
    const storedUser = localStorage.getItem("user");
    
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    
    if (storedAccessToken) {
      setAccessToken(storedAccessToken);
    }
    
    if (storedRefreshToken) {
      setRefreshToken(storedRefreshToken);
    }
    
    // Only fetch priorities if we have tokens
    if (storedAccessToken && storedRefreshToken) {
      getDataStats("get_priorities", storedAccessToken, storedRefreshToken)
      .then((res) => {
        if (res && res.priorities) {
          let prioritiesList = res.priorities.split(",").filter(item => item.length)
          setSelectedUnmet(
            prioritiesList.map((item) => ({
              label: selectLabels[item] ? selectLabels[item] : item,
              value: item,
            }))
          );
        }
      })
      .catch((Err) => {
        console.log("Error fetching priorities:", Err);
      });
    }
  }, []); // Run only once on initial mount, not when tokens change

  const navigate = useNavigate();
  const loginAction = async (data) => {
    try {
      console.log("Attempting login...");
      const response = await fetch("https://clinica-server.replit.app/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...data }),
      });
      const res = await response.json();
      if (res.message === "Login successful") {
        let userData = {
          ...data,
          region: res.region,
          company: res.company,
          admin: res.admin,
          page_view: res.page_view,
          name: res.name
        };
        
        // Set the token and user data in localStorage first
        localStorage.setItem("accessToken", res.access_token);
        localStorage.setItem("refreshToken", res.refresh_token);
        localStorage.setItem("user", JSON.stringify(userData));
        
        // Then update state
        setUser(userData);
        setAccessToken(res.access_token);
        setRefreshToken(res.refresh_token);
        
        console.log("Login successful, tokens stored in localStorage");
        
        if (res.admin) {
          dispatch(
            updateMenu({
              currentMenu: "users",
              currentMenuLabel: "Users",
            })
          );
        } else {
          dispatch(
            updateMenu({
              currentMenu: "home",
              currentMenuLabel: "Home",
            })
          );
        }
        navigate("/dashboard", {
          replace: true
        });
        return;
      } else {
        console.error("Login failed:", res.message || "Unknown error");
        throw new Error(res.message || "Login failed");
      }
    } catch (err) {
      console.error("Login error:", err);
      throw err;
    }
  };


  const updatePassword =async (data) => {
    try {
      const response = await fetch("https://clinica-server.replit.app/update_password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          email: user.email,
          new_password: data.newPassword
         }),
      });
      const res = await response.json();
      return res
    } catch (err) {
      throw new Error();
    }
  }

  const sendEmail =async (data) => {
    try {
      const response = await fetch("https://clinica-server.replit.app/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // 'Access-Control-Allow-Origin': '*',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          message: data.message
         }),
      });
      const res = await response.json();
      return res
    } catch (err) {
      console.log(err)
      throw new Error();
    }
  }


  const logOut = () => {
    setUser(null);
    setAccessToken(null);
    setRefreshToken(null);
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    dispatch(
      updateMenu({
        currentMenu: "users",
        currentMenuLabel: "Users",
      })
    );
    navigate("/", {replace: true});
  };
 

  const fetchUserData = async (token, refreshToken) => {
    try {
      const response = await fetch(
        "https://clinica-server.replit.app/get_all_users",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const userData = await response.json();

        dispatch(addMultipleUsers(userData));
      } else {
        if (response.status === 401) {
          logOut()
          // Unauthorized error
          // Refresh the token
          const refreshedToken = await refreshTokenFunction(refreshToken);
          if (refreshedToken) {
            // Retry fetching with the new token
            await fetchUserData(refreshedToken, refreshToken);
          } else {
            console.error("Failed to refresh token");
            // Handle token refresh failure
          }
        } else {
          console.error("Failed to fetch user data:", response.statusText);
        }
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };


  

  return (
    <AuthContext.Provider
      value={{
        accessToken,
        refreshToken,
        user,
        isAuthenticated,
        sendEmail,
        selectedUnmet,
        setSelectedUnmet,
        updatePassword,
        refreshTokenFunction,
        fetchUserData,
        loginAction,
        logOut,
      }}
    >
      <Outlet />
    </AuthContext.Provider>
  );
};

export default AuthProvider;




export const refreshTokenFunction = async (refreshToken) => {
  try {
    console.log("Attempting to refresh token with refresh token");
    
    // Check if refresh token is valid
    if (!refreshToken) {
      console.error("No refresh token provided");
      return null;
    }
    
    const response = await fetch(
      "https://clinica-server.replit.app/token/refresh",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${refreshToken}`,
        },
        body: JSON.stringify({
          refresh_token: refreshToken,
        }),
      }
    );
    
    if (response.ok) {
      const data = await response.json();
      
      // Update the token in localStorage
      localStorage.setItem("accessToken", data.access_token);
      
      // Get access to the current AuthContext instance if available
      const authContextElement = document.querySelector('div[data-auth-context="true"]');
      if (authContextElement) {
        const authContextId = authContextElement.getAttribute('data-auth-context-id');
        if (authContextId && window._authContexts && window._authContexts[authContextId]) {
          // Update the token in the global auth context state
          window._authContexts[authContextId].setAccessToken(data.access_token);
        }
      }
      
      console.log("Token refresh successful, new token stored");
      return data.access_token;
    } else {
      console.error("Failed to refresh token:", response.statusText);
      // Clear invalid tokens
      localStorage.removeItem("accessToken");
      return null;
    }
  } catch (error) {
    console.error("Error refreshing token:", error);
    return null;
  }
};

export { default as RequireAuth } from './RequireAuth';
